import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Package, Pencil, Plus, X } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { AlertBanner, ContentCard, DataTable, EmptyState, LoadingState, PageHeader, SearchInput, ui } from "../../../components/ui";
import { Button } from "../../../components/ui/button";
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from "../../../components/ui/dialog";
import { TablePagination } from "../../../components/ui/TablePagination";
import { cn } from "../../../lib/utils";
import { LeaveActionToast } from "../../leave-management/components/LeaveActionToast";
import {
  addProductService,
  getProductService,
  listProductServices,
  updateProductService,
  updateProductServiceStatus,
} from "../../../services/fsmService";

const K = "fsmProductServices.";
const PAGE_SIZE = 10;
const NAME_MAX = 100;
export const STATUS_TABS = [
  { key: "all", label: "common.all", isActive: undefined },
  { key: "active", label: "status.active", isActive: true },
  { key: "inactive", label: "status.inactive", isActive: false },
];
export const fieldLabelClass = "block text-xs font-semibold per-file-hint mb-1.5";
export const inputClass = "w-full h-10 px-3 rounded-xl border bg-white text-sm outline-hidden focus:ring-1 focus:ring-accent";
export const iconBtnClass = "h-8 w-8 p-0 rounded-lg border-(--color-text-border)";

/** Add/edit dialog shell (Holidays style), shared with the complaint types page. */
export function FormDialog({ open, title, banner, submitting, submitLabel, onClose, onSubmit, children }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !submitting) onClose(); }}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-xs" />
        <DialogPrimitive.Content aria-describedby={undefined} className={cn(
          "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
          "w-[95vw] sm:w-[570px] max-w-[570px] max-h-[90vh] overflow-hidden flex flex-col",
          "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60",
          "font-sans duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        )}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-(--color-text-border)/70 shrink-0">
            <DialogTitle className="text-[18px] font-bold text-foreground tracking-tight">{title}</DialogTitle>
            <button type="button" onClick={onClose} disabled={submitting} aria-label={t("formActions.close")}
              className="h-8 w-8 rounded-lg grid place-items-center text-muted-foreground hover:bg-workspace">
              <X className="h-4 w-4" />
            </button>
          </div>

          <form onSubmit={onSubmit} aria-busy={submitting} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {banner ? <div role="alert" className="bg-error/10 border border-error/25 rounded-xl px-4 py-3 text-xs text-error">{banner}</div> : null}

            {children}

            <div className="pt-4 border-t border-(--color-text-border)/70 flex justify-end gap-3">
              <Button variant="outline" onClick={onClose} disabled={submitting}
                className="h-11 px-6 rounded-xl border-primary/30 text-primary font-semibold text-sm hover:bg-workspace/40 bg-white">
                {t("formActions.cancel")}
              </Button>
              <Button type="submit" disabled={submitting}
                className="h-11 px-6 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-accent transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] min-w-[120px]">
                {submitting ? t(`${K}saving`) : submitLabel}
              </Button>
            </div>
          </form>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

/** All / Active / Inactive tabs (Shifts style). */
export function StatusTabs({ value, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1 border-b border-(--color-text-border)/70 sm:border-b-0">
      {STATUS_TABS.map((tab) => {
        const selected = value === tab.key;
        return (
          <button key={tab.key} type="button" onClick={() => onChange(tab.key)}
            className={`inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold border-b-2 transition ${
              selected ? "border-primary text-primary" : "border-transparent per-file-hint hover:text-foreground"}`}>
            {t(tab.label)}
          </button>
        );
      })}
    </div>
  );
}

/** Active/Inactive switch with badge. Stops the click so it never triggers a clickable row. */
export function StatusToggle({ active, name, disabled, onToggle }) {
  const { t } = useTranslation();
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }} disabled={disabled}
      aria-label={t(active ? `${K}markInactive` : `${K}markActive`, { name })}
      className="inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
      <span className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${active ? "bg-primary" : "bg-gray-200"}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${active ? "left-[18px]" : "left-0.5"}`} />
      </span>
      <span className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap tracking-wide",
        active ? "bg-(--status-published-bg) text-(--status-published-fg)" : "bg-(--status-archived-bg) text-(--status-archived-fg)"
      )}>
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {t(active ? "status.active" : "status.inactive")}
      </span>
    </button>
  );
}

/** `record` is the detail response when editing, null when adding. */
function ProductServiceDialog({ open, record, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit = Boolean(record);
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState("");
  const [banner, setBanner] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(record?.productServiceName ?? "");
    setIsActive(true);
    setError("");
    setBanner("");
  }, [open, record]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    const trimmed = name.trim();
    const nextError = !trimmed
      ? t("validation.fieldRequired", { field: t(`${K}name`) })
      : trimmed.length > NAME_MAX ? t(`${K}nameTooLong`, { max: NAME_MAX }) : "";
    setError(nextError);
    if (nextError) return;

    setSubmitting(true);
    setBanner("");
    try {
      if (isEdit) {
        await updateProductService(record.productServiceId, { productServiceName: trimmed, isActive: record.isActive });
      } else {
        await addProductService({ productServiceName: trimmed, isActive });
      }
      onSaved(isEdit);
    } catch (err) {
      setBanner(err?.message || t(`${K}saveFailed`));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <FormDialog open={open} title={t(isEdit ? `${K}editItem` : `${K}newItem`)} banner={banner} submitting={submitting}
      submitLabel={t(isEdit ? "formActions.save" : "formActions.create")} onClose={onClose} onSubmit={handleSubmit}>
      <div>
        <label className={fieldLabelClass}>{t(`${K}name`)} <span className="text-error">*</span></label>
        <input value={name} onChange={(e) => { setName(e.target.value); setError(""); }}
          className={cn(inputClass, error ? "border-error" : "border-(--color-text-border)")} />
        {error ? <p className="text-[11px] text-error mt-1">{error}</p> : null}
      </div>

      {!isEdit ? (
        <div>
          <label className={fieldLabelClass}>{t("status.status")}</label>
          <StatusToggle active={isActive} name={name} disabled={submitting} onToggle={() => setIsActive((current) => !current)} />
        </div>
      ) : null}
    </FormDialog>
  );
}

export function ProductServicesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [name, setName] = useState("");
  const [statusTab, setStatusTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);
  const [dialog, setDialog] = useState({ open: false, record: null });
  const [busyId, setBusyId] = useState(null);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true);
    setError("");
    try {
      const { isActive } = STATUS_TABS.find((tab) => tab.key === statusTab);
      const result = await listProductServices({ name, isActive, page: page - 1, size: PAGE_SIZE });
      if (request !== requestRef.current) return;
      setRows(result.content);
      setTotalItems(result.totalElements);
      setTotalPages(Math.max(1, result.totalPages));
    } catch (err) {
      if (request !== requestRef.current) return;
      setRows([]);
      setError(err?.message || t(`${K}loadFailed`));
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [name, statusTab, page, t]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => load();

  // The API filters by name or by status, not both, so picking one clears the other.
  const selectTab = (key) => { setStatusTab(key); setName(""); setPage(1); };
  const searchName = (value) => { setName(value); setStatusTab("all"); setPage(1); };

  const openComplaintTypes = (row) => navigate(`/fsm/products-services/${row.productServiceId}/complaints`);

  async function openEdit(id) {
    setBusyId(id);
    try {
      setDialog({ open: true, record: await getProductService(id) });
    } catch (err) {
      setToast({ type: "error", message: err?.message || t(`${K}detailFailed`) });
    } finally {
      setBusyId(null);
    }
  }

  async function handleToggle(row) {
    setBusyId(row.productServiceId);
    try {
      await updateProductServiceStatus(row.productServiceId, !row.isActive);
      setToast({ type: "success", message: t(`${K}statusUpdated`) });
      refresh();
    } catch (err) {
      setToast({ type: "error", message: err?.message || t(`${K}statusFailed`) });
    } finally {
      setBusyId(null);
    }
  }

  function handleSaved(isEdit) {
    setDialog({ open: false, record: null });
    setToast({ type: "success", message: t(isEdit ? `${K}updated` : `${K}created`) });
    refresh();
  }

  const editButton = (row) => (
    <Button variant="outline" className={iconBtnClass} disabled={busyId === row.productServiceId}
      onClick={(e) => { e.stopPropagation(); openEdit(row.productServiceId); }} aria-label={t("common.edit")}>
      <Pencil className="h-3.5 w-3.5 text-primary" />
    </Button>
  );

  const statusToggle = (row) => (
    <StatusToggle active={Boolean(row.isActive)} name={row.productServiceName}
      disabled={busyId === row.productServiceId} onToggle={() => handleToggle(row)} />
  );

  return (
    <AppLayout overlay={<LeaveActionToast toast={toast} />}>
      <PageHeader back={<PageBackLink to="/" />}
        title={t(`${K}pageTitle`)}
        action={
          <Button onClick={() => setDialog({ open: true, record: null })} className={ui.primaryBtn}>
            <Plus className="h-4 w-4" />
            {t(`${K}newItem`)}
          </Button>
        } />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <StatusTabs value={statusTab} onChange={selectTab} />
        <SearchInput submitOnEnter value={name} placeholder={t("formActions.searchByName")}
          onChange={(e) => searchName(e.target.value.trim())} />
      </div>

      {error ? <AlertBanner type="error">{error}</AlertBanner> : null}

      <ContentCard>
        {loading ? (
          <LoadingState message={t("common.loading")} />
        ) : rows.length === 0 ? (
          <EmptyState icon={Package} title={t(`${K}noItems`)}
            message={t(name || statusTab !== "all" ? `${K}noMatch` : `${K}getStarted`)} />
        ) : (
          <>
            <DataTable minWidth="640px" columns={[
              { key: "name", label: t(`${K}name`) },
              { key: "status", label: t("status.status") },
              { key: "actions", label: t("common.actions"), className: "text-right" },
            ]}>
              {rows.map((row) => (
                <tr key={row.productServiceId} onClick={() => openComplaintTypes(row)}
                  className="hover:bg-workspace/20 transition-colors cursor-pointer">
                  <td className="px-4 py-3.5">
                    <p className="font-semibold text-foreground text-sm truncate max-w-[420px]">{row.productServiceName || "—"}</p>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">{statusToggle(row)}</td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1.5">{editButton(row)}</div>
                  </td>
                </tr>
              ))}
            </DataTable>

            <div className="md:hidden space-y-4 p-4">
              {rows.map((row) => (
                <div key={row.productServiceId} onClick={() => openComplaintTypes(row)}
                  className="p-4 rounded-xl border border-(--color-text-border)/50 bg-white shadow-xs flex flex-col gap-3 cursor-pointer hover:border-primary/40 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <p className="min-w-0 font-semibold text-foreground text-sm truncate">{row.productServiceName || "—"}</p>
                    {editButton(row)}
                  </div>
                  {statusToggle(row)}
                </div>
              ))}
            </div>

            <TablePagination page={page} totalPages={totalPages} totalItems={totalItems}
              pageSize={PAGE_SIZE} onPageChange={setPage} />
          </>
        )}
      </ContentCard>

      <ProductServiceDialog open={dialog.open} record={dialog.record}
        onClose={() => setDialog({ open: false, record: null })} onSaved={handleSaved} />
    </AppLayout>
  );
}
